/// <reference path="ReconciliationEntryState.ts" />

interface InvoiceLineReconciliationCellDelegate
{
    /// item: the product or concept item that took the account, when the line did not keep it
    lineDidChangeAccounts(cell:InvoiceLineReconciliationCell, line:InvoiceLine, item?:CoreItem):void;
}

// A line of an invoice in the reconciliation list, with the ledger account and classification it
// books with. A line without an account of its own inherits it ("↳"): its invoice's default, else
// its classification's account, else its product's or concept item's, the order of the journal
// templates' "line or document ledger account" key (and, on a purchase line without product, the
// expenditure account the server falls back to). An account picked for a line goes, by default, to
// its product or concept item, so every line of it books with it; the line keeps it for itself
// when it is a special case. The empty row of the account picker brings the inheritance back.
class InvoiceLineReconciliationCell extends UITableViewCell
{
    delegate:InvoiceLineReconciliationCellDelegate = null;

    /// The state of the invoice's journal entry, counting its other lines (set by the list).
    documentState:ReconciliationEntryState = null;

    private conceptLabel:MUILabel = null;
    private accountDropdown:MUIButton = null;
    private classificationDropdown:MUIButton = null;
    private vatLabel:MUILabel = null;
    private baseAmountLabel:MUILabel = null;
    private totalAmountLabel:MUILabel = null;

    awakeFromHTML(){
        this.conceptLabel = MUIOutlet(this, "concept-lbl", "MUILabel");
        this.vatLabel = MUIOutlet(this, "vat-lbl", "MUILabel");
        this.baseAmountLabel = MUIOutlet(this, "base-amount-lbl", "MUILabel");
        this.totalAmountLabel = MUIOutlet(this, "total-amount-lbl", "MUILabel");

        this.accountDropdown = MUIOutlet(this, "account-dd", "MUIButton");
        this.accountDropdown.setAction(this, this.showSelectLedgerAccount);

        this.classificationDropdown = MUIOutlet(this, "classification-dd", "MUIButton");
        this.classificationDropdown.setAction(this, this.showSelectClassification);

        // the invoice's head shows the selection
        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _item:InvoiceLine = null;
    set item(item:InvoiceLine) {
        this._item = item;
        this.update();
    }
    get item():InvoiceLine { return this._item; }

    /// Marks the last line of an invoice, which closes its card.
    set isLastInSection(value:boolean) {
        if (value) MUICoreLayerAddStyle(this.layer, "rec-last");
        else MUICoreLayerRemoveStyle(this.layer, "rec-last");
    }

    update() {
        let line = this._item;
        if (line == null) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let escape = ReconciliationEntryStateHelper.escape;
        let state = this.entryState;

        // in red until the invoice has a journal entry
        this.conceptLabel.text = escape(line.concept);
        if (state == ReconciliationEntryState.none) MUICoreLayerAddStyle(this.conceptLabel.layer, "alert");
        else MUICoreLayerRemoveStyle(this.conceptLabel.layer, "alert");

        if (line.ledgerAccount != null) {
            this.accountDropdown.title = ReconciliationEntryStateHelper.accountTitle(line.ledgerAccount);
            MUICoreLayerRemoveStyle(this.accountDropdown.layer, "rec-inherited");
        }
        else {
            this.accountDropdown.title = this.inheritedAccountTitle();
            MUICoreLayerAddStyle(this.accountDropdown.layer, "rec-inherited");
        }

        let classification = line.lineClassification;
        this.classificationDropdown.title = classification != null ? escape(classification.title) : MIOLocalizeString("CHOOSE", "Choose");
        if (classification != null) MUICoreLayerRemoveStyle(this.classificationDropdown.layer, "rec-inherited");
        else MUICoreLayerAddStyle(this.classificationDropdown.layer, "rec-inherited");

        this.vatLabel.text = ad.numberFormatter.stringFromNumber((line.vatFactor ?? 0) * 100) + "%";
        this.baseAmountLabel.text = ad.currencyFormatter.stringFromNumber(line.baseAmount ?? 0);
        this.totalAmountLabel.text = ad.currencyFormatter.stringFromNumber(line.totalAmount ?? 0);

        // a locked entry is not changed from its lines
        let locked = state == ReconciliationEntryState.locked;
        for (let dropdown of [this.accountDropdown, this.classificationDropdown]) {
            dropdown.enabled = !locked;
            if (locked) MUICoreLayerAddStyle(dropdown.layer, "disable");
            else MUICoreLayerRemoveStyle(dropdown.layer, "disable");
        }
    }

    private get entryState() : ReconciliationEntryState {
        return this.documentState ?? ReconciliationEntryStateHelper.stateForItem(this._item?.document);
    }

    /// The account the line books with when it has none of its own.
    static inheritedAccount(line:InvoiceLine) : LedgerAccount {
        if (line == null) return null;
        let purchase = line instanceof PurchaseInvoiceLine;
        let product = line.product as any;
        let conceptItem = line.conceptItem as any;
        let account = line.document?.ledgerAccount
            ?? line.lineClassification?.ledgerAccount
            ?? (purchase ? product?.purchaseLedgerAccount : product?.salesLedgerAccount)
            ?? (purchase ? conceptItem?.purchaseLedgerAccount : conceptItem?.salesLedgerAccount)
            ?? null;
        if (account != null || !purchase || product != null) return account;

        // a purchase line without product books to the expenditure account (the server's fallback)
        let document = line.document as any;
        return (line as any).expenditureLedgerAccount
            ?? document?.legalEntity?.expenditureLedgerAccount
            ?? document?.expenditureLedgerAccount
            ?? null;
    }

    private inheritedAccountTitle() : string {
        let account = InvoiceLineReconciliationCell.inheritedAccount(this._item);
        // nothing on the client: the journal template decides
        let title = account != null ? ReconciliationEntryStateHelper.accountTitle(account) : MIOLocalizeString("AS_IN_THE_JOURNAL_TEMPLATE", "As in the journal template");
        return "↳ " + title;
    }

    private showSelectLedgerAccount() {
        let line = this._item;
        if (line == null || this.entryState == ReconciliationEntryState.locked) return;

        AppHelper.sharedInstance().showSelectLedgerAccountViewControllerFromView(this.accountDropdown, line.ledgerAccount, null, false, null, this, function(this:InvoiceLineReconciliationCell, controller:SelectEntityViewController, account:LedgerAccount){
            // another picker may answer while this one was asked for (see AppHelper)
            if (account != null && !(account instanceof LedgerAccount)) return;
            // the empty row (inherit again) and a line without product or concept item: the line's own
            if (account == null || InvoiceLineReconciliationCell.relatedItem(line) == null) {
                this.setLineAccount(line, account);
                return;
            }
            this.askWhereTheAccountGoes(line, account);
        }, this.inheritedAccountTitle());
    }

    /// The product or concept item of the line: its account is the default of every line of it.
    private static relatedItem(line:InvoiceLine) : CoreItem {
        return line?.product ?? line?.conceptItem ?? null;
    }

    // An account picked for a line is, by default, the one of its product or concept item (every
    // line of it, in any invoice, books with it); the line keeps it for itself as a special case.
    private askWhereTheAccountGoes(line:InvoiceLine, account:LedgerAccount) {
        let item = InvoiceLineReconciliationCell.relatedItem(line);
        let product = item instanceof Product;
        let itemTitle = MIOLocalizeString(product ? "FOR_THE_PRODUCT" : "FOR_THE_CONCEPT", product ? "For the product" : "For the concept") + " «" + (item.name ?? "") + "»";

        let avc = new MUIAlertViewController();
        avc.initWithTitle(ReconciliationEntryStateHelper.accountTitle(account), MIOLocalizeString("THE_ACCOUNT_OF_THE_PRODUCT_OR_CONCEPT_IS_FOR_ALL_ITS_LINES", "The account of the product or concept is for all its lines, in every invoice; the line's own, for this line only"), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(itemTitle, MUIAlertActionStyle.Default, this, function(this:InvoiceLineReconciliationCell){
            this.setItemAccount(line, item, account);
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ONLY_FOR_THIS_LINE", "Only for this line"), MUIAlertActionStyle.Default, this, function(this:InvoiceLineReconciliationCell){
            this.setLineAccount(line, account);
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        AppHelper.sharedInstance().presentViewController(avc, true);
    }

    private setLineAccount(line:InvoiceLine, account:LedgerAccount) {
        if (line.ledgerAccount == account) return;
        line.ledgerAccount = account;
        line.ledgerAccountName = account?.name ?? null;
        this.update();
        this.delegate?.lineDidChangeAccounts(this, line);
    }

    // The sales or purchase account of the item, as the line is. The line's own account goes with
    // it: the line inherits the item's, after its invoice's default, as every line of the item.
    private setItemAccount(line:InvoiceLine, item:CoreItem, account:LedgerAccount) {
        let purchase = line instanceof PurchaseInvoiceLine;
        let current = purchase ? item.purchaseLedgerAccount : item.salesLedgerAccount;
        if (current == account && line.ledgerAccount == null) { this.update(); return; }

        if (purchase) item.purchaseLedgerAccount = account;
        else item.salesLedgerAccount = account;
        if (item instanceof Product) {
            // the product screen keeps the title of the account next to it
            if (purchase) item.purchaseLedgerAccountName = account.description;
            else item.salesLedgerAccountName = account.description;
        }
        line.ledgerAccount = null;
        line.ledgerAccountName = null;
        this.update();
        this.delegate?.lineDidChangeAccounts(this, line, item);
    }

    private showSelectClassification() {
        let line = this._item;
        if (line == null || this.entryState == ReconciliationEntryState.locked) return;

        AppHelper.sharedInstance().showCustomSelectViewControllerFromView(this.classificationDropdown, "DBDocumentLineClassification", "title", null, this, function(this:InvoiceLineReconciliationCell, controller:SelectEntityViewController, classification:DBDocumentLineClassification){
            if (classification != null && !(classification instanceof DBDocumentLineClassification)) return;
            if (line.lineClassification == classification) return;
            line.lineClassification = classification;
            this.update();
            this.delegate?.lineDidChangeAccounts(this, line);
        }, true, MIOLocalizeString("NONE", "None"), line.lineClassification);
    }
}
